import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Loader2, Wand2, Music2, Upload, ImageIcon,
  RotateCcw, Download, Library, Check, PenLine, Disc3, Users,
  Clapperboard, KeyRound, ExternalLink, X, Share2, FolderLock, Scissors,
  AudioLines,
} from "lucide-react";
import { musicApi, pipelinesApi, streamAsset, downloadAsset, pipelineErr, guardToast, mistApi } from "@/api/mist";
import { emergencyCommit } from "@/components/mist/UniversalSaveModal";
import { socialApi } from "@/api/social";
import { CoinBalanceChip } from "@/components/mist/shared";
import VaultPickButton from "@/components/mist/VaultPickButton";
import { useSession } from "@/context/SessionContext";
import { consumeDirectorPrompt, getSongOrigin, setSongResult, clearSongOrigin } from "@/lib/directorPrefill";

const CACHE_KEY = "mist_music_studio_v1";

const LYRIC_FORMATS = [
  { k: "V-C-V-C-B-C", label: "Standard · Verse / Chorus" },
  { k: "V-C-V-C", label: "Loop · Verse / Chorus ×2" },
  { k: "V-PC-C-V-PC-C-B-C", label: "Narrative · Pre-Chorus build" },
  { k: "V-C-V", label: "AABA · Short-form" },
];

// Expand a structure code (e.g. "V-C-V-C-B-C") into ordered, writable sections.
// Repeated types (chorus / pre-chorus) share a `type` so lyrics can auto-mirror.
function buildSections(code) {
  let vN = 0;
  return (code || "V-C-V-C-B-C").split("-").map((tok, i) => {
    const t = (tok || "").trim().toUpperCase();
    let label, type;
    if (t === "V") { vN += 1; label = `Verse ${vN}`; type = `verse${vN}`; }
    else if (t === "PC") { label = "Pre-Chorus"; type = "prechorus"; }
    else if (t === "C") { label = "Chorus"; type = "chorus"; }
    else if (t === "B") { label = "Bridge"; type = "bridge"; }
    else if (t === "I") { label = "Intro"; type = "intro"; }
    else if (t === "O") { label = "Outro"; type = "outro"; }
    else { label = t || "Section"; type = `s${i}`; }
    return { id: `${type}-${i}`, label, type, text: "", dirty: false };
  });
}
function assembleLyrics(sections) {
  return (sections || []).map((s) => `[${s.label}]\n${(s.text || "").trim()}`).join("\n\n").trim();
}

// Parse a free-typed arrangement ("Intro - Verse - Chorus - Pre-Chorus - Chorus")
// into a token code buildSections + the backend understand ("I-V-C-PC-C").
// Scans left-to-right so multi-word sections ("pre-chorus") survive any separator.
function parseStructureInput(raw) {
  const re = /pre[\s-]?chorus|verse|chorus|hook|bridge|intro|outro|\bpc\b|\bv\b|\bc\b|\bb\b|\bi\b|\bo\b/gi;
  const codes = [];
  let m;
  while ((m = re.exec(raw || "")) !== null) {
    const t = m[0].toLowerCase().replace(/[\s-]/g, "");
    if (t === "prechorus" || t === "pc") codes.push("PC");
    else if (t === "verse" || t === "v") codes.push("V");
    else if (t === "chorus" || t === "hook" || t === "c") codes.push("C");
    else if (t === "bridge" || t === "b") codes.push("B");
    else if (t === "intro" || t === "i") codes.push("I");
    else if (t === "outro" || t === "o") codes.push("O");
  }
  return codes.join("-");
}
// Readable chips for a token code, e.g. "I-V-C" -> ["Intro","Verse 1","Chorus"].
function structurePreview(code) {
  let vN = 0;
  return (code || "").split("-").filter(Boolean).map((t) => {
    const u = t.toUpperCase();
    if (u === "V") { vN += 1; return `Verse ${vN}`; }
    if (u === "PC") return "Pre-Chorus";
    if (u === "C") return "Chorus";
    if (u === "B") return "Bridge";
    if (u === "I") return "Intro";
    if (u === "O") return "Outro";
    return t;
  });
}
// Guess where the first chorus lands (seconds) from the structure + track length.
function estimateChorusStart(code, dur) {
  const toks = (code || "V-C-V-C-B-C").split("-").filter(Boolean);
  const idx = toks.findIndex((t) => t.toUpperCase() === "C");
  const frac = idx > 0 && toks.length > 1 ? idx / toks.length : 0.3;
  return Math.max(0, Math.min(dur * frac, Math.max(0, dur - 15)));
}
const fmtTime = (s) => { s = Math.max(0, Math.round(s || 0)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };

function StructureChips({ code }) {
  const chips = structurePreview(code);
  if (!chips.length) {
    return <p className="mt-1.5 font-mono text-[8px] text-white/40">Type sections in order — e.g. Verse, Chorus, Pre-Chorus, Bridge, Intro, Outro.</p>;
  }
  return (
    <div className="mt-1.5 flex flex-wrap gap-1" data-testid="studio-structure-preview">
      {chips.map((c, i) => (
        <span key={i} className="font-mono text-[8px] font-black uppercase tracking-widest bg-[#00F5D4]/15 text-[#00F5D4] border border-[#00F5D4]/40 px-1.5 py-0.5 rounded">{c}</span>
      ))}
    </div>
  );
}

const GENRES = ["Pop", "Rock", "EDM", "Hip Hop", "R&B", "Jazz", "Blues", "Country", "K-Pop", "Dance", "Synth", "Metal", "Cinematic", "Folk", "Latin"];
// Free-text genre suggestions (users can type ANYTHING — e.g. "Synth pop",
// "acoustic", "country" — for a more specific, better-matched sound).
const GENRE_SUGGEST = [...new Set(["Synth pop", "Indie rock", "Acoustic", "Country", "Lo-fi hip hop", "Afrobeats", "House", "Ambient", "Gospel", "Reggaeton", "Drill", "Trap soul", "Bossa nova", "Punk", ...GENRES])];
const MOODS = ["Romantic", "Nostalgic", "Empowered", "Chill", "Cinematic", "Dark Pop", "Uplifting", "Melancholic"];
const LENGTHS = [
  { v: 30, l: "30s" }, { v: 60, l: "60s" }, { v: 180, l: "180s" }, { v: 240, l: "240s" },
];
const CAMERA_MOVES = [
  { k: "zoom_in", label: "Slow Cinematic Zoom" },
  { k: "orbit", label: "Dynamic Orbiting Pan" },
  { k: "steady_forward", label: "Steady-Cam Forward" },
  { k: "pulse", label: "Audio-Reactive Pulse" },
  { k: "pan_lr", label: "Tracking Pan (L→R)" },
  { k: "crane_up", label: "Crane-Up Reveal" },
];

const STEPS = ["Setup", "Lyrics", "Produce", "Deliver"];

// ── Genre multi-tag picker + format policy (WS1 5-step flow) ──────────────
// The genre field is a searchable multi-tag picker. The selected tags decide
// which LYRIC FORMAT is forced, whether BPM shows, and the default tempo.
const STYLE_TAGS = [
  "Techno", "Minimal", "Tech House", "Deep House", "Deep", "Industrial", "Dark", "Doom",
  "Hypnotic", "Melodic House", "EDM", "Festival House", "Dance", "K-Pop", "Hip Hop", "Rap",
  "Trap", "Pop", "Rock", "Country", "Jazz", "Ballad", "Viking Doom",
];
const MINIMAL_TAGS = ["Techno", "Minimal", "Tech House", "Deep House", "Deep", "Industrial", "Dark", "Doom", "Hypnotic", "Viking Doom"];
const FESTIVAL_TAGS = ["EDM", "Festival House", "Dance", "Melodic House"];
const DENSE_TAGS = ["Hip Hop", "Rap", "Trap"];
const FORMAT_LABELS = {
  MINIMAL_LOOPS: "Minimalistic • Loops / Textures",
  FESTIVAL_HOOK_DROP: "Festival Ready • Hook & Drop",
  DENSE_BARS: "Continuous Dense Bars",
  KPOP_DYNAMIC: "K-Pop • Dynamic Pop-Dance",
  LOFI_ACOUSTIC: "Chill Lofi • Acoustic Topline",
  "V-C-V-C-B-C": "Standard • Verse / Chorus",
};
// Genre → default BPM (editable). Falls back to 128 for any BPM-visible tag not listed.
const BPM_MAP = {
  "Techno": 140, "Industrial": 140, "Hypnotic": 140,
  "Tech House": 126, "Deep House": 126, "Deep": 126,
  "Minimal": 128, "Dark": 125, "Doom": 90, "Viking Doom": 95,
  "EDM": 128, "Festival House": 128, "Dance": 128, "Melodic House": 124, "K-Pop": 122,
};
function defaultBpm(tags) {
  for (const t of tags) {
    const k = Object.keys(BPM_MAP).find((x) => x.toLowerCase() === t.toLowerCase());
    if (k) return BPM_MAP[k];
  }
  return 128;
}
// Returns the forced LYRIC FORMAT + whether BPM shows, for the chosen genre tags.
function formatPolicy(tags) {
  const has = (list) => tags.some((t) => list.some((x) => x.toLowerCase() === t.toLowerCase()));
  const one = (k) => ({ options: [{ k, label: FORMAT_LABELS[k] }], def: k, allowCustom: false, restricted: true });
  if (!tags.length) return { ...one("LOFI_ACOUSTIC"), showBpm: false, hypnotic: false };
  if (has(MINIMAL_TAGS)) return { ...one("MINIMAL_LOOPS"), showBpm: true, hypnotic: true };
  if (has(FESTIVAL_TAGS)) return { ...one("FESTIVAL_HOOK_DROP"), showBpm: true, hypnotic: true };
  if (has(DENSE_TAGS)) return { ...one("DENSE_BARS"), showBpm: false, hypnotic: false };
  if (has(["K-Pop"])) return { ...one("KPOP_DYNAMIC"), showBpm: false, hypnotic: false };
  // Ungrouped genres (Pop / Rock / Country / Jazz / Ballad) → full pop layout.
  return {
    options: [
      { k: "V-C-V-C-B-C", label: FORMAT_LABELS["V-C-V-C-B-C"] },
      ...LYRIC_FORMATS.filter((f) => f.k !== "V-C-V-C-B-C"),
    ],
    def: "V-C-V-C-B-C", allowCustom: true, restricted: false, showBpm: false, hypnotic: false,
  };
}
const ENGINES = [
  { k: "suno", label: "Suno" },
  { k: "udio", label: "Udio" },
  { k: "minimax", label: "MiniMax" },
];

function StepBar({ phase }) {
  return (
    <div className="flex items-center px-4 pb-2" data-testid="studio-stepbar">
      {STEPS.map((s, i) => {
        const n = i + 1;
        const done = phase > n;
        const active = phase === n;
        return (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-1.5">
              <span className={`w-6 h-6 grid place-items-center rounded-full border-2 border-black font-mono text-[10px] font-black ${done ? "bg-[#9EFFB8] text-black" : active ? "bg-[#F9E27D] text-black" : "bg-charcoal text-white/50"}`}>
                {done ? <Check size={12} strokeWidth={3} /> : n}
              </span>
              <span className={`font-mono text-[8px] uppercase tracking-widest ${active || done ? "text-white/80" : "text-white/35"}`}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="flex-1 h-[2px] bg-[#2a2a2a] mx-1.5" />}
          </div>
        );
      })}
    </div>
  );
}

/* Artwork tile — streams the uploaded/vault cover, or shows a disc placeholder. */
function Artwork({ assetId, size = "full" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (assetId) streamAsset(assetId).then((u) => { url = u; setSrc(u); }).catch(() => {});
    else setSrc(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [assetId]);
  const cls = size === "sm"
    ? "w-24 h-24 rounded-md"
    : "w-40 h-40 rounded-lg";
  if (!assetId) {
    return (
      <div data-testid="studio-artwork-placeholder"
        className={`${cls} grid place-items-center border-4 border-black bg-gradient-to-br from-[#141414] to-charcoal shadow-[4px_4px_0px_#000]`}>
        <Disc3 size={size === "sm" ? 34 : 56} className="text-[#00F5D4]/70" />
      </div>
    );
  }
  return src
    ? <img src={src} alt="album artwork" data-testid="studio-artwork"
        className={`${cls} object-cover border-4 border-black shadow-[4px_4px_0px_#000]`} />
    : <div className={`${cls} grid place-items-center border-4 border-black bg-charcoal shadow-[4px_4px_0px_#000]`}>
        <Loader2 className="animate-spin text-[#00F5D4]" size={20} />
      </div>;
}

function StudioAudio({ assetId, autoPlay, onDuration }) {
  const [src, setSrc] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    let url;
    streamAsset(assetId).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [assetId]);
  useEffect(() => {
    if (src && autoPlay && ref.current) { ref.current.play().catch(() => {}); }
  }, [src, autoPlay]);
  if (!src) return <div className="h-12 grid place-items-center"><Loader2 className="animate-spin text-gold" /></div>;
  return <audio ref={ref} src={src} controls data-testid="studio-audio" className="w-full"
    onLoadedMetadata={(e) => onDuration && onDuration(e.currentTarget.duration || 0)} />;
}

function StudioVideo({ assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    streamAsset(assetId).then((u) => { url = u; setSrc(u); }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [assetId]);
  if (!src) return <div className="aspect-video grid place-items-center"><Loader2 className="animate-spin text-[#B69CFF]" /></div>;
  return <video src={src} controls autoPlay loop playsInline data-testid="studio-video" className="w-full border-2 border-black" />;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full border-2 border-black bg-[#141414] text-white placeholder:text-white/40 px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-gold shadow-[3px_3px_0px_#000]";

export default function MistMusicStudio({ onBack }) {
  const session = useSession();
  const realName = (session?.user?.name || "").trim();

  const [phase, setPhase] = useState(1);
  const [songOrigin] = useState(() => getSongOrigin()); // Create-Song round-trip: where we came from
  // Phase 1
  const [lyricMode, setLyricMode] = useState("ai"); // ai = AI writes | own = write my own
  const [sections, setSections] = useState(() => buildSections("V-C-V-C-B-C"));
  const [songName, setSongName] = useState(() => getSongOrigin()?.projectName || "");
  const [coverAssetId, setCoverAssetId] = useState("");
  const [styleTags, setStyleTags] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const stylePrompt = styleTags.join(", ");
  const addTag = (raw) => {
    let t = (raw || "").trim();
    if (!t) return;
    const canon = STYLE_TAGS.find((x) => x.toLowerCase() === t.toLowerCase());
    if (canon) t = canon;
    setStyleTags((cur) => (cur.some((x) => x.toLowerCase() === t.toLowerCase()) ? cur : [...cur, t]));
    setTagQuery("");
  };
  const removeTag = (t) => setStyleTags((cur) => cur.filter((x) => x !== t));
  const [storyline, setStoryline] = useState(() => consumeDirectorPrompt());
  const [lyricFormat, setLyricFormat] = useState("V-C-V-C-B-C");
  const [customStructure, setCustomStructure] = useState("");
  const [genre, setGenre] = useState("");
  const [bpm, setBpm] = useState(0);
  const [mood, setMood] = useState("Empowered");
  const [vocalDensity, setVocalDensity] = useState(""); // "" auto | Punchy (Dance Fit) | Ultra-Short (Loops)
  const [busyUpload, setBusyUpload] = useState(false);
  // Phase 2
  const [trackId, setTrackId] = useState(null);
  const [lyrics, setLyrics] = useState("");
  const [vocalGender, setVocalGender] = useState("female"); // female | duet | male
  const [backing, setBacking] = useState(false);
  const [length, setLength] = useState(180);
  const [engine, setEngine] = useState("suno");   // Suno (default) | Udio | MiniMax (ElevenLabs = silent fallback)
  const [instrumental, setInstrumental] = useState(false);
  // Auto fade-out the last ~2s so vocals never end abruptly. Persisted across
  // sessions (defaults ON); user can disable per the reported cutoff issue.
  const [autoFade, setAutoFade] = useState(() => {
    try { const v = localStorage.getItem("mist_auto_fade"); return v === null ? true : v === "1"; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("mist_auto_fade", autoFade ? "1" : "0"); } catch { /* ignore */ }
  }, [autoFade]);
  const [busyLyrics, setBusyLyrics] = useState(false);
  const [fixingIdx, setFixingIdx] = useState(null);   // section index being AI-polished
  const [ownBoxes, setOwnBoxes] = useState([""]);     // "write your own" free-form section boxes
  const [fixingBox, setFixingBox] = useState(null);   // own-box index being AI-polished
  // Phase 3/4
  const [busyProduce, setBusyProduce] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [shared, setShared] = useState(false);
  const [busyShare, setBusyShare] = useState(false);
  const [busyVault, setBusyVault] = useState(false);
  const [savedVault, setSavedVault] = useState(false);
  const [busyDownload, setBusyDownload] = useState(false);
  // Chorus-hook trimmer (Deliver phase) — auto-set to the chorus, user can nudge.
  const [audioDur, setAudioDur] = useState(0);
  const [hookStart, setHookStart] = useState(0);
  const [hookLen, setHookLen] = useState(20);
  const [hookTouched, setHookTouched] = useState(false);
  const [busyHook, setBusyHook] = useState(false);
  const [savedTrims, setSavedTrims] = useState([]);
  // Feed share + BYOK music video (Deliver phase)
  const [busyFeed, setBusyFeed] = useState(false);
  const [sharedFeed, setSharedFeed] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoKey, setVideoKey] = useState("");
  const [keyStatus, setKeyStatus] = useState(null); // null | checking | valid | invalid
  const [systemKey, setSystemKey] = useState(null); // null=unknown, true=saved key available
  const [camera, setCamera] = useState("zoom_in");
  const [busyVideo, setBusyVideo] = useState(false);
  const [videoResult, setVideoResult] = useState(null);
  const uploadRef = useRef(null);
  const hydrated = useRef(false);
  const progTimer = useRef(null);

  // ---- persist all step state (survives accidental refresh) ----
  useEffect(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}");
      if (c && (c.songName || c.trackId)) {
        setSongName(c.songName || ""); setCoverAssetId(c.coverAssetId || "");
        setStyleTags(Array.isArray(c.styleTags) ? c.styleTags : (c.stylePrompt ? String(c.stylePrompt).split(",").map((s) => s.trim()).filter(Boolean) : [])); setStoryline(c.storyline || "");
        setLyricFormat(c.lyricFormat || "V-C-V-C-B-C"); setGenre(c.genre || "Pop");
        setCustomStructure(c.customStructure || "");
        setMood(c.mood || "Empowered"); setTrackId(c.trackId || null);
        setLyricMode(c.lyricMode || "ai");
        if (Array.isArray(c.sections) && c.sections.length) setSections(c.sections);
        setLyrics(c.lyrics || ""); setVocalGender(c.vocalGender || "female");
        setBacking(!!c.backing);
        setLength(c.length || 60); setInstrumental(!!c.instrumental);
        setPhase(c.phase && c.phase < 4 ? c.phase : (c.phase || 1));
      }
    } catch { /* ignore */ }
    hydrated.current = true;
  }, []);
  useEffect(() => {
    if (!hydrated.current) return;
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        songName, coverAssetId, styleTags, storyline, lyricFormat, genre, mood,
        trackId, lyrics, vocalGender, backing, length, instrumental, lyricMode, sections,
        customStructure,
        phase: phase >= 4 ? 3 : phase,
      }));
    } catch { /* ignore */ }
  }, [songName, coverAssetId, styleTags, storyline, lyricFormat, genre, mood, trackId, lyrics, vocalGender, backing, length, instrumental, lyricMode, sections, phase]);

  // WS1 → session interlock: the genre tags force the lyric format AND overwrite
  // the downstream genre + BPM so no stale 'Pop' default leaks into the audio pipeline.
  useEffect(() => {
    const pol = formatPolicy(styleTags);
    setLyricFormat((cur) => (pol.options.some((o) => o.k === cur) || (pol.allowCustom && cur === "CUSTOM") ? cur : pol.def));
    if (styleTags.length) {
      setGenre(styleTags[0]);
      setBpm(pol.showBpm ? defaultBpm(styleTags) : 0);
    }
  }, [styleTags]);

  const resetAll = useCallback(() => {
    try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
    setPhase(1); setSongName(""); setCoverAssetId(""); setStyleTags([]); setStoryline("");
    setLyricFormat("V-C-V-C-B-C"); setGenre(""); setBpm(0); setMood("Empowered");
    setCustomStructure("");
    setLyricMode("ai"); setSections(buildSections("V-C-V-C-B-C"));
    setTrackId(null); setLyrics(""); setVocalGender("female"); setBacking(false); setLength(180); setInstrumental(false);
    setEngine("suno"); setOwnBoxes([""]);
    setProgress(0); setResult(null); setShared(false);
    setAudioDur(0); setHookStart(0); setHookLen(20); setHookTouched(false); setBusyHook(false); setSavedTrims([]);
    setSharedFeed(false); setVideoOpen(false); setVideoKey(""); setKeyStatus(null);
    setCamera("zoom_in"); setVideoResult(null);
  }, []);

  // ------- Phase 1: image anchor upload -------
  const onUploadImage = async (e) => {
    const file = (e.target.files || [])[0]; e.target.value = "";
    if (!file) return;
    setBusyUpload(true);
    try {
      const r = await pipelinesApi.uploadSeed(file);
      setCoverAssetId(r.asset_id);
      toast.success("Visual anchor set");
    } catch { toast.error("Upload failed"); }
    finally { setBusyUpload(false); }
  };

  // ------- "Write my own" section editors -------
  const applyFormat = (code) => {
    setLyricFormat(code);
    setSections((prev) => {
      const fresh = buildSections(code);
      const used = {};
      return fresh.map((s) => {
        const matches = prev.filter((p) => p.type === s.type);
        const k = used[s.type] = (used[s.type] || 0);
        used[s.type] = k + 1;
        const old = matches[k];
        return old ? { ...s, text: old.text, dirty: old.dirty } : s;
      });
    });
  };
  const editSection = (idx, val) => {
    setSections((prev) => {
      const cur = prev[idx];
      const next = prev.map((s, i) => (i === idx ? { ...s, text: val, dirty: true } : s));
      // Repeated sections (chorus / pre-chorus) auto-mirror into later spots that
      // the user hasn't personally edited yet.
      if (cur.type === "chorus" || cur.type === "prechorus") {
        return next.map((s, i) => (i !== idx && s.type === cur.type && !s.dirty ? { ...s, text: val } : s));
      }
      return next;
    });
  };
  const switchMode = (mode) => {
    setLyricMode(mode);
    if (mode === "own" && !sections.some((s) => s.text)) setSections(buildSections(lyricFormat === "CUSTOM" ? parseStructureInput(customStructure) : lyricFormat));
  };
  // Effective structure code sent to the backend (custom string → token code).
  const effStructure = () => (lyricFormat === "CUSTOM" ? parseStructureInput(customStructure) : lyricFormat);
  // Rebuild the "write my own" section editors from a custom typed arrangement,
  // preserving text the user already wrote (matched by section type).
  const rebuildCustomSections = () => {
    const code = parseStructureInput(customStructure);
    if (!code) return;
    setSections((prev) => {
      const fresh = buildSections(code);
      const used = {};
      return fresh.map((s) => {
        const matches = prev.filter((p) => p.type === s.type);
        const k = used[s.type] = (used[s.type] || 0);
        used[s.type] = k + 1;
        const old = matches[k];
        return old ? { ...s, text: old.text, dirty: old.dirty } : s;
      });
    });
  };
  // Pick "Custom" from the structure picker (own mode) / dropdown (AI mode).
  const pickCustomStructure = () => {
    setLyricFormat("CUSTOM");
    const code = parseStructureInput(customStructure);
    if (code) rebuildCustomSections();
  };
  // AI-polish a single section (verse / first chorus) for a consistent song flow.
  const fixSection = async (i) => {
    const s = sections[i];
    if (!s || !(s.text || "").trim()) { toast.error("Write a little of this part first"); return; }
    setFixingIdx(i);
    try {
      const r = await musicApi.fixSection({ text: s.text, section_label: s.label, section_type: s.type, genre, mood });
      if (r?.text) { editSection(i, r.text); toast.success(`${s.label} polished ✓`); }
      else toast.error("Couldn't refine that part");
    } catch (e) { toast.error(pipelineErr(e, "Couldn't refine that part")); }
    finally { setFixingIdx(null); }
  };
  // Show "AI fix" on every editable section EXCEPT repeated chorus/pre-chorus
  // boxes — those auto-mirror from the first, so fixing the first covers them.
  const showFix = (s, i) =>
    (s.type === "chorus" || s.type === "prechorus")
      ? i === sections.findIndex((x) => x.type === s.type)
      : true;

  // ------- "Write my own" Phase 1 → 2: lock lyrics, provision track -------
  const startOwnLyrics = async () => {
    if (!songName.trim()) { toast.error("Give your song a name first"); return; }
    const text = (ownBoxes[0] || "").trim();
    if (!text) { toast.error("Write your lyrics first"); return; }
    setBusyLyrics(true);
    try {
      const r = await musicApi.lyrics({ own_lyrics: text, structure: "CUSTOM", mood, genre, length });
      setTrackId(r.track_id); setLyrics(r.lyrics || text);
      setPhase(2);
      toast.success("Lyrics locked — add your song details");
    } catch (e) {
      toast.error(e?.response?.data?.detail || pipelineErr(e, "Couldn't save your lyrics"));
    } finally { setBusyLyrics(false); }
  };
  // AI-polish a single "write your own" box in place.
  const fixOwnBox = async (i) => {
    const t = (ownBoxes[i] || "").trim();
    if (!t) { toast.error("Write a little of this part first"); return; }
    setFixingBox(i);
    try {
      const r = await musicApi.fixSection({ text: t, section_label: "Section", section_type: "verse", genre, mood });
      if (r?.text) { setOwnBoxes((b) => b.map((x, idx) => (idx === i ? r.text : x))); toast.success("Polished ✓"); }
      else toast.error("Couldn't refine that part");
    } catch (e) { toast.error(pipelineErr(e, "Couldn't refine that part")); }
    finally { setFixingBox(null); }
  };

  // ------- Phase 1 → 2: generate lyrics -------
  const generateLyrics = async () => {
    if (!songName.trim()) { toast.error("Give your song a name first"); return; }
    if (!coverAssetId && !storyline.trim() && !stylePrompt.trim()) {
      toast.error("Add an image, a style, or a storyline"); return;
    }
    const structCode = effStructure();
    if (lyricFormat === "CUSTOM" && !structCode) { toast.error("Type your song structure — e.g. Verse - Chorus - Verse - Chorus"); return; }
    setBusyLyrics(true);
    try {
      const hasText = storyline.trim() || stylePrompt.trim();
      if (coverAssetId && !hasText) {
        // Image only (no typed prompt) — fall back to image-driven lyrics.
        const fd = new FormData();
        fd.append("asset_id", coverAssetId);
        fd.append("structure", structCode || lyricFormat);
        fd.append("length", String(length));
        const r = await musicApi.lyricsFromImage(fd);
        setTrackId(r.track_id); setLyrics(r.lyrics || "");
        if (r.genre) setGenre((g) => (GENRES.find((x) => x.toUpperCase() === r.genre.toUpperCase()) || r.genre || g));
      } else {
        // Typed prompt ALWAYS dominates; image stays secondary context + artwork.
        const concept = [
          songName.trim() ? `Title: "${songName.trim()}".` : "",
          stylePrompt.trim() ? `Genre: ${stylePrompt.trim()}.` : "",
          bpm ? `Tempo: ${bpm} BPM.` : "",
          storyline.trim(),
        ].filter(Boolean).join(" ");
        const r = await musicApi.lyrics({ prompt: concept, mood, structure: structCode || lyricFormat, genre, length, vocal_density: vocalDensity });
        setTrackId(r.track_id); setLyrics(r.lyrics || "");
      }
      setPhase(2);
      toast.success("Lyrics ready — edit anything you like");
    } catch (e) {
      toast.error(e?.response?.data?.detail || pipelineErr(e, "Lyric generation failed"));
    } finally { setBusyLyrics(false); }
  };

  // ------- Phase 2 → 3/4: produce track -------
  const startProgress = () => {
    setProgress(6);
    if (progTimer.current) clearInterval(progTimer.current);
    progTimer.current = setInterval(() => {
      setProgress((p) => (p >= 92 ? 92 : p + Math.max(0.6, (92 - p) * 0.045)));
    }, 700);
  };
  const stopProgress = (final) => {
    if (progTimer.current) { clearInterval(progTimer.current); progTimer.current = null; }
    setProgress(final);
  };
  useEffect(() => () => { if (progTimer.current) clearInterval(progTimer.current); }, []);

  // Auto-position the chorus hook once the finished track's duration is known
  // (unless the user has already nudged the handles).
  useEffect(() => {
    if (!result || !audioDur || hookTouched) return;
    const code = lyricFormat === "CUSTOM" ? parseStructureInput(customStructure) : lyricFormat;
    const start = estimateChorusStart(code, audioDur);
    setHookStart(Math.round(start));
    setHookLen((L) => Math.min(L, Math.max(15, Math.floor(audioDur))));
  }, [result, audioDur, hookTouched, lyricFormat, customStructure]);

  // Detect a saved/founder MiniMax key so the BYOK prompt can be skipped.
  useEffect(() => {
    if (videoOpen && systemKey === null) {
      pipelinesApi.videoKeyStatus().then((r) => setSystemKey(!!r.available)).catch(() => setSystemKey(false));
    }
  }, [videoOpen, systemKey]);

  const produceTrack = async () => {
    if (!trackId || !lyrics.trim()) { toast.error("Lyrics are empty"); return; }
    setBusyProduce(true); setResult(null); setShared(false);
    setPhase(4);
    startProgress();
    // Compose the backend vocal arrangement: duet is a standalone macro; backing
    // upgrades a single lead to its "_backing" variant.
    const vocal = vocalGender === "duet"
      ? "duet"
      : backing ? `${vocalGender}_backing` : vocalGender;
    try {
      const { job_id } = await pipelinesApi.musicProduce({
        track_id: trackId, lyrics, title: songName.trim() || "Untitled Track",
        producer: realName ? `Prod. By ${realName}` : "",
        length, genre, genres: [genre], vocal, engine,
        cover_asset_id: coverAssetId || "", instrumental,
        auto_fade: autoFade, bpm: bpm || 0, mood,
      });
      const j = await pipelinesApi.poll(job_id, { timeout: 600000 });
      if (j.status === "completed" && j.asset_id) {
        stopProgress(100);
        setResult({ assetId: j.asset_id, engine: j.engine });
        setSavedVault(false);
        guardToast(j);
        // MD Manual-Saving-Law: nothing auto-saves. Track is staged — the user
        // saves it explicitly below (or the disconnect sweeper catches it).
        toast.success("Track ready — save it to keep it in your Vault");
      } else {
        stopProgress(0); setPhase(2);
        toast.error(j.error || "Produce failed");
      }
    } catch (e) {
      stopProgress(0); setPhase(2);
      toast.error(pipelineErr(e, "Produce failed"));
    } finally { setBusyProduce(false); }
  };

  // ------- Phase 4 actions -------
  const saveToDevice = async () => {
    if (!result) return;
    if (!songName.trim()) { toast.error("Add a song title before saving"); return; }
    setBusyDownload(true);
    try {
      await downloadAsset(result.assetId, songName.trim() || "mist_track", "mp3");
      toast.success("Saved to device ✓");
    } catch { toast.error("Download failed"); }
    finally { setBusyDownload(false); }
  };
  const saveToStock = async () => {
    if (!result || shared) return;
    if (!songName.trim()) { toast.error("Add a song title before sharing"); return; }
    setBusyShare(true);
    try {
      await mistApi.shareCommunity(result.assetId, (genre || "OTHER").toUpperCase());
      setShared(true);
      toast.success("Published to the Shared Music Library ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't publish"); }
    finally { setBusyShare(false); }
  };
  const saveToVault = async () => {
    if (!result || savedVault) return;
    setBusyVault(true);
    try {
      await mistApi.commitAsset(result.assetId);
      setSavedVault(true);
      toast.success("Saved to Mist Vault ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save to Mist Vault"); }
    finally { setBusyVault(false); }
  };
  // Create-Song round-trip: hand the finished track back to the surface that
  // launched us (Singer Studio Workspace 1 or Music Viz) and return there.
  const returnToProject = (carry) => {
    if (carry && result) {
      setSongResult({ assetId: result.assetId, title: songName.trim() || songOrigin?.projectName || "AI Track" });
      try { emergencyCommit(result.assetId); } catch { /* best-effort persist before we leave */ }
    }
    const search = songOrigin?.returnUrl || "";
    clearSongOrigin();
    window.history.pushState({}, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  // Hand the finished track to Music Viz (visualizer) and jump there.
  const openInMusicViz = () => {
    if (!result) return;
    if (!songName.trim()) { toast.error("Add a song title first"); return; }
    setSongResult({ assetId: result.assetId, title: songName.trim() || "Mist track" });
    try { emergencyCommit(result.assetId); } catch { /* best-effort persist */ }
    window.history.pushState({}, "", `${window.location.pathname}?studio=directormode&dstab=viz`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  // MD Manual-Saving-Law — disconnect safety net: if the user leaves with an
  // unsaved (staged) track, commit it so generation credits are never lost.
  useEffect(() => {
    const id = result?.assetId;
    if (!id || savedVault) return undefined;
    const h = () => emergencyCommit(id);
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [result, savedVault]);
  // Stateful multi-trim — save AS MANY cuts as you like without a page reset.
  // Each /trim call re-reads the read-only master track, so the source is never
  // mutated; saved cuts stack up below for consecutive download / reuse.
  const saveTrim = async () => {
    if (!result || busyHook) return;
    const start = Number(Math.max(0, hookStart).toFixed(2));
    const end = Number(Math.min(hookStart + hookLen, audioDur).toFixed(2));
    if (end - start < 1) { toast.error("Clip is too short — move the start back"); return; }
    setBusyHook(true);
    try {
      const r = await musicApi.trim(result.assetId, start, end, `${songName.trim() || "Track"} ${hookLen}s cut`);
      const aid = r?.asset_id || r?.asset?.id;
      if (!aid) throw new Error("no asset");
      setSavedTrims((list) => [{ assetId: aid, size: hookLen, start, label: `${hookLen}s · from ${fmtTime(start)}` }, ...list]);
      toast.success(`${hookLen}s cut saved to Community Share ✓`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save the cut"); }
    finally { setBusyHook(false); }
  };
  const downloadTrim = async (t) => {
    try { await downloadAsset(t.assetId, `${(songName.trim() || "track").replace(/\s+/g, "_")}_${t.size}s`, "mp3"); }
    catch { toast.error("Couldn't download that cut"); }
  };
  const shareToFeed = async () => {
    if (!result || sharedFeed) return;
    setBusyFeed(true);
    try {
      // Prefer the animated music video (cover art in motion) as the feed media;
      // otherwise the song posts with its cover art as the card background.
      const shareId = videoResult ? videoResult.assetId : result.assetId;
      const caption = `🎵 ${songName.trim() || "New track"}${realName ? ` — Prod. By ${realName}` : ""}`;
      await socialApi.shareToFeed(shareId, caption);
      setSharedFeed(true);
      toast.success("Shared to the feed ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't share to feed"); }
    finally { setBusyFeed(false); }
  };

  const verifyKey = async () => {
    if (!videoKey.trim()) return;
    setKeyStatus("checking");
    try {
      const v = await pipelinesApi.verifyVideoKey(videoKey.trim());
      setKeyStatus(v.valid ? "valid" : "invalid");
      toast[v.valid ? "success" : "error"](v.valid ? "MiniMax key verified ✓" : (v.message || "Invalid key"));
    } catch { setKeyStatus("invalid"); toast.error("Key check failed"); }
  };
  const genVideo = async () => {
    if (!coverAssetId) { toast.error("A cover image is required as the anchor frame"); return; }
    if (!systemKey) {
      if (!videoKey.trim()) { toast.error("Paste your MiniMax video API key"); return; }
      if (keyStatus !== "valid") {
        setKeyStatus("checking");
        try {
          const v = await pipelinesApi.verifyVideoKey(videoKey.trim());
          if (!v.valid) { setKeyStatus("invalid"); toast.error(v.message || "Invalid key"); return; }
          setKeyStatus("valid");
        } catch { setKeyStatus("invalid"); toast.error("Key check failed"); return; }
      }
    }
    setBusyVideo(true); setVideoResult(null);
    try {
      const { job_id } = await pipelinesApi.musicVideo({
        cover_asset_id: coverAssetId, camera, api_key: systemKey ? "" : videoKey.trim(), context: `${genre} ${mood}`,
      });
      const j = await pipelinesApi.poll(job_id, { timeout: 600000, interval: 6000 });
      if (j.status === "completed" && j.asset_id) {
        setVideoResult({ assetId: j.asset_id });
        guardToast(j);
        toast.success("Music video ready 🎬");
      } else { toast.error(j.error || "Video generation failed"); }
    } catch (e) {
      toast.error(pipelineErr(e, "Video generation failed"));
    } finally { setBusyVideo(false); }
  };

  const noInputs = !coverAssetId && !storyline.trim() && !stylePrompt.trim();
  const policy = formatPolicy(styleTags);

  return (
    <div className="h-full w-full flex flex-col" data-testid="mist-music-studio">
      <div className="shrink-0 px-4 pt-3 pb-1 flex items-center justify-between">
        <button
          onClick={songOrigin && phase === 1 ? () => returnToProject(false) : (phase === 1 ? onBack : () => setPhase((p) => (p === 4 ? 2 : p - 1)))}
          data-testid="studio-back"
          className="flex items-center gap-1.5 min-w-0 truncate font-mono text-[9px] font-black uppercase tracking-widest text-white/60">
          <ArrowLeft size={13} className="shrink-0" /> <span className="truncate">{phase === 1 ? (songOrigin ? `Back to ${songOrigin.label || "Project"}` : "Music Studios") : "Back"}</span>
        </button>
        <div className="flex items-center gap-2">
          <button onClick={resetAll} data-testid="studio-reset"
            className="flex items-center gap-1 font-mono text-[8px] font-black uppercase tracking-widest text-white/40">
            <RotateCcw size={11} /> Reset
          </button>
          <CoinBalanceChip />
        </div>
      </div>

      <StepBar phase={phase} />

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-28 pt-2 space-y-4">
        {/* ============================ PHASE 1 ============================ */}
        {phase === 1 && (
          <div className="space-y-4" data-testid="studio-phase-1">
            <div className="grid grid-cols-2 gap-2" data-testid="studio-lyric-mode">
              {[{ k: "ai", label: "✨ AI Writes" }, { k: "own", label: "✍️ Write Your Own" }].map((m) => (
                <button key={m.k} onClick={() => switchMode(m.k)} data-testid={`studio-mode-${m.k}`} aria-pressed={lyricMode === m.k}
                  className={`border-2 border-black py-3 font-mono text-[11px] font-black uppercase tracking-wide shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${lyricMode === m.k ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/60"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <h3 className="font-serif text-2xl text-white">{lyricMode === "own" ? "Write your song" : "Set up your song"}</h3>

            <Field label="Song name *">
              <input value={songName} onChange={(e) => setSongName(e.target.value)} data-testid="studio-song-name"
                placeholder="e.g. Rise of the Vanguard" className={inputCls} />
            </Field>

            {/* Lyric-source mode tabs live at the very top (above song name) */}

            {/* ===================== WRITE MY OWN (WS2: plain manual input only) ===================== */}
            {lyricMode === "own" && (
              <div className="space-y-4" data-testid="studio-own-writer">
                <div>
                  <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Your lyrics — type everything exactly as you want it</span>
                  <textarea value={ownBoxes[0] || ""} onChange={(e) => setOwnBoxes([e.target.value])} rows={14}
                    data-testid="studio-own-box-0"
                    placeholder={"Write your full lyrics here…\n\n[Verse]\n…\n\n[Chorus]\n…"}
                    className={`${inputCls} resize-y leading-relaxed`} />
                  <p className="mt-1.5 font-mono text-[8px] text-white/40">No auto-formatting — what you type is exactly what gets sung.</p>
                </div>

                <button onClick={startOwnLyrics} disabled={busyLyrics || !songName.trim()} data-testid="studio-own-next"
                  className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
                  {busyLyrics ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Next: Song Details
                </button>
              </div>
            )}

            {/* ===================== AI WRITES ===================== */}
            {lyricMode === "ai" && (<>
            <div>
              <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Album cover (optional) — secondary context; your typed prompt always leads</span>
              <div className="flex items-center gap-3">
                <Artwork assetId={coverAssetId} size="sm" />
                <div className="flex-1 flex flex-col gap-2">
                  <button onClick={() => uploadRef.current?.click()} disabled={busyUpload} data-testid="studio-upload-image"
                    className="flex items-center justify-center gap-2 bg-charcoal text-[#00F5D4] font-black font-mono text-[10px] uppercase tracking-widest py-2.5 border-2 border-black rounded-lg shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">
                    {busyUpload ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload image
                  </button>
                  <VaultPickButton bucket="images" testid="studio-image-vault" label="From Vault"
                    onItem={(item) => { setCoverAssetId(item.id); toast.success("Visual anchor set"); }}
                    className="flex items-center justify-center gap-1.5 border-2 border-black bg-[#00F5D4] text-black px-3 py-2.5 font-mono text-[10px] font-black uppercase tracking-widest rounded-lg active:translate-y-[1px]" />
                  {coverAssetId && (
                    <button onClick={() => setCoverAssetId("")} data-testid="studio-clear-image"
                      className="font-mono text-[8px] uppercase tracking-widest text-white/40">Remove image</button>
                  )}
                </div>
              </div>
              <input ref={uploadRef} type="file" hidden accept="image/*" onChange={onUploadImage} data-testid="studio-image-input" />
            </div>

            <Field label="Genre (searchable — pick one or more)">
              {styleTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2" data-testid="studio-style-tags">
                  {styleTags.map((t) => (
                    <button key={t} type="button" onClick={() => removeTag(t)}
                      data-testid={`studio-style-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
                      className="flex items-center gap-1 border-2 border-black bg-[#00F5D4] text-black px-2 py-1 font-mono text-[9px] font-black uppercase tracking-widest shadow-[2px_2px_0px_#000]">
                      {t} <X size={10} />
                    </button>
                  ))}
                </div>
              )}
              <input value={tagQuery} list="style-tag-suggest" data-testid="studio-style-prompt"
                onChange={(e) => { const v = e.target.value; if (STYLE_TAGS.includes(v)) addTag(v); else setTagQuery(v); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagQuery); } }}
                placeholder="Search genres — Techno, Festival House, K-Pop, Rap, Ballad…" className={inputCls} />
              <datalist id="style-tag-suggest">
                {STYLE_TAGS.filter((t) => !styleTags.includes(t)).map((t) => <option key={t} value={t} />)}
              </datalist>
            </Field>

            {policy.showBpm && (
              <Field label="BPM (tempo)">
                <input type="number" min="60" max="200" value={bpm}
                  onChange={(e) => setBpm(Number(e.target.value) || 0)}
                  data-testid="studio-bpm" className={`${inputCls} font-black tracking-wide`} />
                <p className="mt-1 font-mono text-[8px] text-white/40">Auto-set from your genre — adjust to taste. Fed straight to the audio engine.</p>
              </Field>
            )}

            <Field label="Storyline / theme (your prompt — always leads)">
              <textarea value={storyline} onChange={(e) => setStoryline(e.target.value.slice(0, 600))} rows={4}
                data-testid="studio-storyline" placeholder={policy.hypnotic ? "Enter a 2-4 word hypnotic theme or atmosphere phrase (e.g., 'System pulse', 'Neon shadows')." : "Describe what the song is about — the story, the feeling, the setting."}
                className={`${inputCls} resize-none`} />
            </Field>

            <Field label="Lyric format">
              <select value={lyricFormat} onChange={(e) => setLyricFormat(e.target.value)} data-testid="studio-lyric-format"
                className={`${inputCls} font-black uppercase tracking-wide`}>
                {policy.options.map((f) => <option key={f.k} value={f.k}>{f.label}</option>)}
                {policy.allowCustom && <option value="CUSTOM">Custom — type your own arrangement</option>}
              </select>
            </Field>

            <Field label="Vocal density">
              <select value={vocalDensity} onChange={(e) => setVocalDensity(e.target.value)} data-testid="studio-vocal-density"
                className={`${inputCls} font-black uppercase tracking-wide`}>
                <option value="">Auto — match track length</option>
                <option value="Punchy (Dance Fit)">Punchy — 2-3 lines per block (dance fit)</option>
                <option value="Ultra-Short (Loops)">Ultra-short — 1 phrase per section (loops)</option>
              </select>
            </Field>
            {policy.allowCustom && lyricFormat === "CUSTOM" && (
              <div data-testid="studio-custom-structure-ai">
                <input value={customStructure} onChange={(e) => setCustomStructure(e.target.value)}
                  data-testid="studio-custom-structure-input"
                  placeholder="e.g. Intro - Verse - Chorus - Verse - Pre-Chorus - Chorus - Bridge - Chorus - Outro"
                  className={inputCls} />
                <StructureChips code={parseStructureInput(customStructure)} />
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <p className="font-mono text-[8px] text-white/35">Mood &amp; engine are set on the next screen.</p>
            </div>

            <button onClick={generateLyrics} disabled={busyLyrics || !songName.trim() || noInputs} data-testid="studio-generate-lyrics"
              className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50">
              {busyLyrics ? <Loader2 size={16} className="animate-spin" /> : <PenLine size={16} />} {busyLyrics ? "Writing lyrics…" : "Generate Lyrics"}
            </button>
            </>)}
          </div>
        )}

        {/* ============================ PHASE 2 ============================ */}
        {phase === 2 && (
          <div className="space-y-4" data-testid="studio-phase-2">
            <h3 className="font-serif text-2xl text-white">{lyricMode === "own" ? "Song details" : "Refine & customize"}</h3>

            <div className="grid grid-cols-2 gap-3" data-testid="studio-details">
              <Field label="Genre">
                <input value={genre} onChange={(e) => setGenre(e.target.value)} data-testid="studio-genre"
                  list="genre-suggest" placeholder="Type any style — e.g. Synth pop, acoustic"
                  className={`${inputCls} font-black uppercase tracking-wide`} />
                <datalist id="genre-suggest">{GENRE_SUGGEST.map((g) => <option key={g} value={g} />)}</datalist>
              </Field>
              <Field label="Mood">
                <select value={mood} onChange={(e) => setMood(e.target.value)} data-testid="studio-mood"
                  className={`${inputCls} font-black uppercase tracking-wide`}>
                  {MOODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Engine">
                <select value={engine} onChange={(e) => setEngine(e.target.value)} data-testid="studio-engine"
                  className={`${inputCls} font-black uppercase tracking-wide`}>
                  {ENGINES.map((en) => <option key={en.k} value={en.k}>{en.label}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Lyrics (editable)">
              <textarea value={lyrics} onChange={(e) => setLyrics(e.target.value)} rows={12} data-testid="studio-lyrics-editor"
                className={`${inputCls} leading-relaxed resize-y whitespace-pre-wrap`} />
            </Field>

            <div>
              <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Lead vocal</span>
              <div className="grid grid-cols-3 gap-2" data-testid="studio-vocal-gender">
                {[{ k: "female", label: "Female" }, { k: "duet", label: "Duet" }, { k: "male", label: "Male" }].map((v) => (
                  <button key={v.k} onClick={() => setVocalGender(v.k)} data-testid={`studio-vocal-${v.k}`} aria-pressed={vocalGender === v.k}
                    disabled={instrumental}
                    className={`border-2 border-black py-2.5 font-mono text-[10px] font-black uppercase tracking-wide shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-40 ${vocalGender === v.k && !instrumental ? "bg-gold text-black" : "bg-[#141414] text-white/60"}`}>{v.label}</button>
                ))}
              </div>
              <button onClick={() => setBacking((x) => !x)} data-testid="studio-backing-toggle" aria-pressed={backing && vocalGender !== "duet"}
                disabled={instrumental || vocalGender === "duet"}
                className={`mt-2 w-full flex items-center justify-between border-2 border-black px-3.5 py-2.5 shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-40 ${backing && !instrumental && vocalGender !== "duet" ? "bg-[#A4DCFA] text-black" : "bg-[#141414] text-white/60"}`}>
                <span className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-wide"><Users size={14} /> Backing vocals</span>
                <span className={`w-9 h-5 rounded-full border-2 border-black flex items-center transition-colors ${backing && vocalGender !== "duet" ? "bg-black/20" : "bg-black/40"}`}>
                  <span className={`w-3.5 h-3.5 rounded-full bg-black transition-transform ${backing && vocalGender !== "duet" ? "translate-x-4" : "translate-x-0.5"}`} />
                </span>
              </button>
              <p className="mt-1 font-mono text-[8px] text-white/45">{vocalGender === "duet" ? "Duet pairs a male & female lead automatically." : "Add layered backing harmonies behind the lead."}</p>
            </div>

            <div>
              <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/60">Track length</span>
              <div className="grid grid-cols-4 gap-1.5" data-testid="studio-lengths">
                {LENGTHS.map((L) => (
                  <button key={L.v} onClick={() => setLength(L.v)} data-testid={`studio-length-${L.v}`} aria-pressed={length === L.v}
                    className={`border-2 border-black py-2.5 font-mono text-[10px] font-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${length === L.v ? "bg-gold text-black" : "bg-[#141414] text-white"}`}>{L.l}</button>
                ))}
              </div>
            </div>

            <button onClick={() => setInstrumental((x) => !x)} data-testid="studio-instrumental-toggle" aria-pressed={instrumental}
              className={`w-full flex items-center justify-between border-2 border-black px-3.5 py-3 shadow-[3px_3px_0px_#000] ${instrumental ? "bg-[#9EFFB8] text-black" : "bg-[#141414] text-white/70"}`}>
              <span className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-wide"><Music2 size={14} /> Instrumental track (no vocals)</span>
              <span className={`w-9 h-5 rounded-full border-2 border-black grid items-center transition-colors ${instrumental ? "bg-black/20" : "bg-black/40"}`}>
                <span className={`w-3.5 h-3.5 rounded-full bg-black transition-transform ${instrumental ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
            </button>

            <button onClick={() => setAutoFade((x) => !x)} data-testid="studio-autofade-toggle" aria-pressed={autoFade}
              className={`w-full flex items-center justify-between border-2 border-black px-3.5 py-3 shadow-[3px_3px_0px_#000] ${autoFade ? "bg-[#00F5D4] text-black" : "bg-[#141414] text-white/70"}`}>
              <span className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-wide"><AudioLines size={14} /> Natural fade-out ending</span>
              <span className={`w-9 h-5 rounded-full border-2 border-black grid items-center transition-colors ${autoFade ? "bg-black/20" : "bg-black/40"}`}>
                <span className={`w-3.5 h-3.5 rounded-full bg-black transition-transform ${autoFade ? "translate-x-4" : "translate-x-0.5"}`} />
              </span>
            </button>
            <p className="-mt-1 font-mono text-[8px] text-white/45">
              {autoFade
                ? `On: a musical ${Math.round(Math.min(12, Math.max(4, 960 / (bpm || 120))) * 10) / 10}s BPM-synced fade resolves the outro instead of a hard cut.`
                : "Off: hard ending — the track stops without a fade."}
            </p>

            <button onClick={produceTrack} disabled={busyProduce || !genre.trim()} data-testid="studio-produce"
              className="w-full flex items-center justify-center gap-2 bg-[#00F5D4] text-black font-black font-mono text-xs uppercase tracking-widest py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed">
              {busyProduce ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Generate Song
            </button>
            {!genre.trim() && (
              <p className="-mt-1 font-mono text-[8px] uppercase tracking-widest text-[#E4572E]">Type a genre above to unlock Generate Song.</p>
            )}
          </div>
        )}

        {/* ======================= PHASE 3/4 (Deliver) ===================== */}
        {phase === 4 && (
          <div className="space-y-5 flex flex-col items-center text-center pt-2" data-testid="studio-phase-4">
            <h3 className="font-serif text-2xl text-white">{result ? songName.trim() || "Your Track" : "Producing…"}</h3>
            <Artwork assetId={coverAssetId} />

            {!result ? (
              <div className="w-full max-w-sm space-y-2" data-testid="studio-progress">
                <div className="w-full h-4 border-2 border-black bg-[#141414] overflow-hidden shadow-[3px_3px_0px_#000]">
                  <div className="h-full bg-gradient-to-r from-[#00F5D4] to-[#9EFFB8] transition-all duration-700 ease-out"
                    style={{ width: `${Math.round(progress)}%` }} data-testid="studio-progress-bar" />
                </div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                  {instrumental ? "Arranging instrumental" : "Recording vocals"} · {Math.round(progress)}%
                </p>
              </div>
            ) : (
              <div className="w-full max-w-sm space-y-4">
                <StudioAudio assetId={result.assetId} autoPlay onDuration={setAudioDur} />
                {realName && <p className="font-mono text-[8px] uppercase tracking-widest text-white/45">Prod. By {realName} · {result.engine || "studio"}</p>}
                {songOrigin && (
                  <button onClick={() => returnToProject(true)} data-testid="studio-use-in-project"
                    className="w-full flex items-center justify-center gap-2 bg-[#FFE08A] text-black font-black font-mono text-[11px] uppercase tracking-widest py-3.5 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-x-[3px] active:translate-y-[3px] active:shadow-none">
                    <ArrowLeft size={15} /> Use in {songOrigin.label || "Project"}
                  </button>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={saveToDevice} disabled={busyDownload} data-testid="studio-save-device"
                    className="flex items-center justify-center gap-2 bg-[#F9E27D] text-black font-black font-mono text-[10px] uppercase tracking-widest py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
                    {busyDownload ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Save to Device
                  </button>
                  <button onClick={saveToStock} disabled={busyShare || shared} data-testid="studio-save-stock"
                    className={`flex items-center justify-center gap-2 font-black font-mono text-[10px] uppercase tracking-widest py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60 ${shared ? "bg-[#9EFFB8] text-black" : "bg-[#00F5D4] text-black"}`}>
                    {busyShare ? <Loader2 size={14} className="animate-spin" /> : shared ? <Check size={14} strokeWidth={3} /> : <Share2 size={14} />} {shared ? "Shared" : "Community Share"}
                  </button>
                </div>
                {!shared && <p className="mt-1 font-mono text-[8px] uppercase tracking-widest text-white/40 text-center" data-testid="studio-save-hint">Save to your device, or share it with the Community</p>}

                <button onClick={openInMusicViz} data-testid="studio-open-musicviz"
                  className="w-full flex items-center justify-center gap-2 bg-[#B69CFF] text-black font-black font-mono text-[10px] uppercase tracking-widest py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
                  <AudioLines size={14} /> Open in Music Viz
                </button>

                {/* ---------- Stateful multi-trim (save many cuts, no reset) ---------- */}
                {audioDur >= 15 && (
                  <div className="border-2 border-black bg-[#141414] shadow-[3px_3px_0px_#000] text-left px-3.5 py-3 space-y-3" data-testid="studio-hook-panel">
                    <p className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-widest text-white/80">
                      <Scissors size={14} className="text-[#F9E27D]" /> Save Cuts <span className="text-white/40 normal-case tracking-normal">· multi-trim</span>
                    </p>
                    <p className="font-mono text-[8px] text-white/45 leading-relaxed">
                      Save as many cuts as you like — pick a length, nudge the start, hit Save. Each cut stacks below to download or reuse. Your master track stays untouched. <span className="text-[#F9E27D] font-black">Trims save to Community Share only</span> (they publish to the Community reel-trim album).
                    </p>
                    <div className="grid grid-cols-3 gap-1.5" data-testid="studio-hook-lengths">
                      {[15, 20, 25, 30, 45, 60].map((L) => (
                        <button key={L} onClick={() => { setHookTouched(true); setHookLen(L); }} data-testid={`studio-hook-len-${L}`} aria-pressed={hookLen === L}
                          disabled={L > audioDur}
                          className={`border-2 border-black py-2 font-mono text-[10px] font-black shadow-[2px_2px_0px_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-40 ${hookLen === L ? "bg-gold text-black" : "bg-[#141414] text-white"}`}>{L}s</button>
                      ))}
                    </div>
                    <div>
                      <div className="flex justify-between font-mono text-[8px] uppercase tracking-widest text-white/50 mb-1">
                        <span data-testid="studio-hook-start-label">Start {fmtTime(hookStart)}</span>
                        <span data-testid="studio-hook-end-label">End {fmtTime(Math.min(hookStart + hookLen, audioDur))}</span>
                      </div>
                      <input type="range" min={0} max={Math.max(0, Math.floor(audioDur - hookLen))} step={1}
                        value={Math.min(hookStart, Math.max(0, Math.floor(audioDur - hookLen)))}
                        onChange={(e) => { setHookTouched(true); setHookStart(Number(e.target.value)); }}
                        data-testid="studio-hook-start" className="w-full accent-[#00F5D4]" />
                    </div>
                    <button onClick={saveTrim} disabled={busyHook} data-testid="studio-save-hook"
                      className="w-full flex items-center justify-center gap-2 font-black font-mono text-[10px] uppercase tracking-widest py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60 bg-[#F9E27D] text-black">
                      {busyHook ? <Loader2 size={14} className="animate-spin" /> : <Scissors size={14} />} Save {hookLen}s Cut
                    </button>
                    {savedTrims.length > 0 && (
                      <div className="space-y-1.5 pt-1" data-testid="studio-saved-trims">
                        <p className="font-mono text-[8px] font-black uppercase tracking-widest text-white/45">Saved cuts ({savedTrims.length})</p>
                        {savedTrims.map((t, i) => (
                          <div key={t.assetId || i} data-testid={`studio-trim-row-${i}`}
                            className="flex items-center justify-between gap-2 border-2 border-black bg-[#0d0d0d] px-2.5 py-2 shadow-[2px_2px_0px_#000]">
                            <span className="flex items-center gap-2 font-mono text-[9px] text-white/75 truncate">
                              <span className="shrink-0 bg-[#00F5D4] text-black px-1.5 py-0.5 border border-black font-black">{t.size}s</span>
                              <span className="truncate">{t.label}</span>
                            </span>
                            <button onClick={() => downloadTrim(t)} data-testid={`studio-trim-download-${i}`}
                              className="shrink-0 flex items-center gap-1 bg-[#9EFFB8] text-black border-2 border-black px-2 py-1 font-mono text-[9px] font-black uppercase active:translate-y-[1px]">
                              <Download size={11} /> MP3
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <button onClick={shareToFeed} disabled={busyFeed || sharedFeed} data-testid="studio-share-feed"
                  className={`w-full flex items-center justify-center gap-2 font-black font-mono text-[10px] uppercase tracking-widest py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60 ${sharedFeed ? "bg-[#9EFFB8] text-black" : "bg-[#B69CFF] text-black"}`}>
                  {busyFeed ? <Loader2 size={14} className="animate-spin" /> : sharedFeed ? <Check size={14} strokeWidth={3} /> : <Share2 size={14} />} {sharedFeed ? "Shared to Feed" : (videoResult ? "Share Video to Feed" : "Share to Feed")}
                </button>

                {/* ---------- BYOK Music Video (cover art → cinematic clip) ---------- */}
                <div className="border-2 border-black bg-[#141414] shadow-[3px_3px_0px_#000] text-left" data-testid="studio-video-panel">
                  <button onClick={() => setVideoOpen((o) => !o)} data-testid="studio-video-toggle"
                    className="w-full flex items-center justify-between px-3.5 py-3 font-mono text-[10px] font-black uppercase tracking-widest text-white/80">
                    <span className="flex items-center gap-2"><Clapperboard size={14} className="text-[#B69CFF]" /> Music Video <span className="text-white/40 normal-case tracking-normal">· BYOK</span></span>
                    <span className="text-white/40">{videoOpen ? "–" : "+"}</span>
                  </button>
                  {videoOpen && (
                    <div className="px-3.5 pb-3.5 space-y-3">
                      {!coverAssetId ? (
                        <p className="font-mono text-[9px] text-white/50 leading-relaxed">Add a cover image back in Setup — the video animates it as the anchor frame.</p>
                      ) : (
                        <>
                          <p className="font-mono text-[9px] leading-relaxed text-white/50">
                            Turn your cover art into a cinematic clip via <span className="text-white/80">MiniMax Hailuo Video-01</span>{systemKey ? "." : ". Your key is used only for this render and never stored."}
                          </p>
                          {systemKey ? (
                            <p className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-[#9EFFB8]" data-testid="studio-video-saved-key">
                              <Check size={12} strokeWidth={3} /> Using your saved MiniMax key
                            </p>
                          ) : (
                          <div>
                            <span className="flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.16em] text-white/50">
                              <span className="flex items-center gap-1"><KeyRound size={11} /> MiniMax API key</span>
                              <a href="https://platform.minimax.io/user-center/basic-information/interface-key" target="_blank" rel="noopener noreferrer"
                                data-testid="studio-video-key-help" className="flex items-center gap-1 text-[#A4DCFA] normal-case tracking-normal underline decoration-dotted">
                                <ExternalLink size={10} /> Get a key
                              </a>
                            </span>
                            <div className="mt-1 flex gap-2">
                              <input type="password" value={videoKey} autoComplete="off"
                                onChange={(e) => { setVideoKey(e.target.value); setKeyStatus(null); }}
                                data-testid="studio-video-key" placeholder="sk-…  (memory only)"
                                className={`flex-1 bg-black text-white placeholder:text-white/40 border-2 px-3 py-2.5 font-mono text-xs outline-none ${keyStatus === "valid" ? "border-[#9EFFB8]" : keyStatus === "invalid" ? "border-red-500" : "border-black"}`} />
                              <button onClick={verifyKey} disabled={!videoKey.trim() || keyStatus === "checking"} data-testid="studio-video-verify"
                                className="shrink-0 grid place-items-center w-12 bg-[#A4DCFA] text-black border-2 border-black font-mono text-[9px] font-black uppercase active:translate-y-[1px] disabled:opacity-50">
                                {keyStatus === "checking" ? <Loader2 size={13} className="animate-spin" /> : keyStatus === "valid" ? <Check size={14} strokeWidth={3} /> : keyStatus === "invalid" ? <X size={14} strokeWidth={3} /> : "Test"}
                              </button>
                            </div>
                          </div>
                          )}
                          <div>
                            <span className="block mb-1.5 font-mono text-[9px] font-black uppercase tracking-[0.16em] text-white/50">Camera movement</span>
                            <select value={camera} onChange={(e) => setCamera(e.target.value)} data-testid="studio-video-camera"
                              className="w-full bg-black text-white border-2 border-black px-2 py-2.5 font-mono text-xs outline-none">
                              {CAMERA_MOVES.map((c) => <option key={c.k} value={c.k}>{c.label}</option>)}
                            </select>
                          </div>
                          <button onClick={genVideo} disabled={busyVideo} data-testid="studio-gen-video"
                            className="w-full flex items-center justify-center gap-2 bg-[#B69CFF] text-black font-black font-mono text-[10px] uppercase tracking-widest py-3 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
                            {busyVideo ? <><Loader2 size={14} className="animate-spin" /> Rendering… (a few min)</> : <><Clapperboard size={14} /> {videoResult ? "Regenerate Video" : "Generate Music Video"}</>}
                          </button>
                          {videoResult && <StudioVideo assetId={videoResult.assetId} />}
                        </>
                      )}
                    </div>
                  )}
                </div>

                <button onClick={resetAll} data-testid="studio-new-track"
                  className="w-full flex items-center justify-center gap-2 font-mono text-[9px] font-black uppercase tracking-widest text-white/50 py-2">
                  <Wand2 size={13} /> Create another track
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
